import { useEffect, useRef, useState } from 'react';
import { Button } from '@astryxdesign/core/Button';
import {
  ChatComposer, ChatLayout, ChatMessage, ChatMessageBubble, ChatMessageList,
} from '@astryxdesign/core/Chat';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { isConfigured, supabase } from '../lib/api';

export interface AssistantSlot {
  service_id: string;
  service_name: string;
  starts_at: string;
  label: string;
}

interface Msg {
  role: 'user' | 'assistant';
  content: string;
  slots?: AssistantSlot[];
  failed?: boolean;
}

interface Props {
  slug: string;
  scope: 'client' | 'owner';
  examples: string[];
  initialQuestion?: string | null;
  intro: string;
  onPickSlot?: (s: AssistantSlot) => void;
  onFallback?: () => void;
}

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/assistant`;

/**
 * Chat with the studio assistant. The model never sees the database: the
 * Edge Function first runs allowed server tools (prices, free slots, the
 * owner's bookings and stats) and only then lets the model answer.
 */
export function AssistantChat({ slug, scope, examples, initialQuestion, intro, onPickSlot, onFallback }: Props) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const asked = useRef(false);

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || pending) return;
    const next: Msg[] = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setDraft('');
    setPending(true);
    try {
      if (!isConfigured) throw new Error('not_configured');
      const { data: s } = await supabase.auth.getSession();
      const token = scope === 'owner' ? s.session?.access_token : import.meta.env.VITE_SUPABASE_ANON_KEY;
      const res = await fetch(FN_URL, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY!,
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          slug,
          scope,
          messages: next.slice(-10).map(({ role, content }) => ({ role, content })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data.error ?? 'assistant_error'), { message_ru: data.message });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, slots: data.slots }]);
    } catch (e) {
      const ru = (e as { message_ru?: string }).message_ru;
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          failed: true,
          content: ru ?? 'Помощник сейчас недоступен. Обычная запись работает — выберите время в форме.',
        },
      ]);
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    if (initialQuestion && !asked.current) {
      asked.current = true;
      void send(initialQuestion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuestion]);

  return (
    <ChatLayout
      composer={
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={(v) => void send(v)}
          placeholder={scope === 'owner' ? 'Например: что у меня завтра?' : 'Спросите про время или цены'}
          isDisabled={pending}
        />
      }
    >
      <ChatMessageList>
        <Text type="supporting" as="p" textWrap="pretty">
          {intro}
        </Text>
        {messages.length === 0 && (
          <VStack gap={2} paddingBlock={2}>
            <Text type="supporting">Можно начать с вопроса:</Text>
            <div className="chip-row">
              {examples.map((q) => (
                <button key={q} type="button" className="chip" onClick={() => void send(q)}>
                  {q}
                </button>
              ))}
            </div>
          </VStack>
        )}
        {messages.map((m, i) => (
          <ChatMessage key={i} sender={m.role}>
            <ChatMessageBubble variant={m.role === 'user' ? 'filled' : 'ghost'}>
              <VStack gap={2}>
                <Text as="p" textWrap="pretty">
                  {m.content}
                </Text>
                {m.slots && m.slots.length > 0 && onPickSlot && (
                  <HStack gap={2} wrap="wrap">
                    {m.slots.slice(0, 6).map((s) => (
                      <Button key={s.starts_at + s.service_id} size="sm" variant="secondary" label={s.label} onClick={() => onPickSlot(s)} />
                    ))}
                  </HStack>
                )}
                {m.failed && onFallback && <Button size="sm" variant="primary" label="Записаться через форму" onClick={onFallback} />}
              </VStack>
            </ChatMessageBubble>
          </ChatMessage>
        ))}
        {pending && (
          <ChatMessage sender="assistant">
            <ChatMessageBubble variant="ghost">
              <Text color="secondary">Смотрю данные студии…</Text>
            </ChatMessageBubble>
          </ChatMessage>
        )}
      </ChatMessageList>
    </ChatLayout>
  );
}
